import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Truck, Shield, Globe, CheckCircle, Tag, Box, FileCheck, Layers, Scale, Building2, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const servicesData = [
  {
    id: 1,
    title: "Special Packing & Labelling",
    description: "We understand that different markets have unique packaging requirements. Our special packing and labelling services ensure your products meet local regulations and appeal to your target customers.",
    image: "https://images.unsplash.com/photo-1644079446600-219068676743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzcxNTkyNDQ4fDA&ixlib=rb-4.1.0&q=85",
    icon: Tag,
    features: [
      "Private label packaging with your brand identity",
      "Multilingual labels compliant with destination country regulations",
      "Barcode and QR code integration for inventory management",
      "Nutritional information and ingredient labels as per FDA/EU standards",
      "Tamper-proof and shelf-appeal packaging options",
      "Custom sizes and pack configurations"
    ],
    highlights: [
      { label: "Label Languages", value: "15+" },
      { label: "Pack Sizes", value: "Custom" },
      { label: "Compliance", value: "FDA/EU/FSSAI" }
    ]
  },
  {
    id: 2,
    title: "Assorted Containers",
    description: "Maximize your shipping efficiency with our assorted container services. We expertly consolidate multiple product categories into single containers, reducing your logistics costs while ensuring safe delivery.",
    image: "https://images.pexels.com/photos/4487382/pexels-photo-4487382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Box,
    features: [
      "Mixed product containers from multiple categories",
      "Optimized loading for maximum space utilization",
      "Temperature-controlled options for sensitive products",
      "Professional palletization and stacking",
      "Full container load (FCL) and less than container load (LCL) options",
      "Real-time tracking and documentation"
    ],
    highlights: [
      { label: "Container Types", value: "20'/40'/40'HC" },
      { label: "Space Utilization", value: "95%+" },
      { label: "Tracking", value: "Real-time" }
    ]
  },
  {
    id: 3,
    title: "Quality Assurance",
    description: "Quality is at the heart of everything we do. Our rigorous quality assurance processes ensure that every product meets international standards before it leaves our facility.",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxMHx8cXVhbGl0eSUyMGNvbnRyb2x8ZW58MHx8fHwxNzcxNTkyNDM0fDA&ixlib=rb-4.1.0&q=85",
    icon: Shield,
    features: [
      "Pre-shipment inspection and quality checks",
      "Laboratory testing for food safety parameters",
      "Shelf-life verification and dating",
      "Certificate of Analysis (COA) for each batch",
      "FSSAI, ISO, and HACCP compliance support",
      "Third-party audit coordination"
    ],
    highlights: [
      { label: "Quality Rate", value: "99.5%" },
      { label: "Certifications", value: "ISO/HACCP" },
      { label: "Testing Labs", value: "NABL Certified" }
    ]
  },
  {
    id: 4,
    title: "Global Logistics",
    description: "From your order confirmation to doorstep delivery, we handle all aspects of international logistics. Our experienced team manages documentation, customs, and freight to ensure smooth and timely delivery.",
    image: "https://images.unsplash.com/photo-1678182451047-196f22a4143e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMGNvbnRhaW5lcnN8ZW58MHx8fHwxNzcxNTkyNDAzfDA&ixlib=rb-4.1.0&q=85",
    icon: Truck,
    features: [
      "Complete export documentation (Invoice, Packing List, B/L, COO)",
      "Customs clearance assistance",
      "Sea freight and air freight options",
      "Port-to-port and door-to-door delivery",
      "Cargo insurance arrangements",
      "Phytosanitary and health certificates"
    ],
    highlights: [
      { label: "Countries", value: "50+" },
      { label: "Ports Served", value: "Major Global" },
      { label: "Documentation", value: "Complete" }
    ]
  }
];

const processSteps = [
  {
    step: '01',
    title: 'Inquiry & Consultation',
    description: 'Share your product requirements, quantities, and destination. Our team provides detailed quotations and guidance.',
    icon: ClipboardCheck
  },
  {
    step: '02',
    title: 'Order Confirmation',
    description: 'Review commercial terms, finalize product specifications, packaging requirements, and delivery schedules.',
    icon: FileCheck
  },
  {
    step: '03',
    title: 'Quality Check & Packing',
    description: 'Products undergo rigorous quality inspection, custom packing, and labelling as per your specifications.',
    icon: Shield
  },
  {
    step: '04',
    title: 'Shipping & Delivery',
    description: 'Complete documentation, customs clearance, and shipment with real-time tracking until delivery.',
    icon: Truck
  }
];

const additionalBenefits = [
  {
    title: "Global Network",
    description: "Established partnerships with shipping lines, freight forwarders, and customs agents across 50+ countries ensure reliable delivery.",
    icon: Globe,
    color: 'var(--gold-accent)'
  },
  {
    title: "Flexible MOQs",
    description: "We work with businesses of all sizes. Whether you need a full container or smaller quantities, we have solutions for you.",
    icon: Scale,
    color: 'var(--primary-teal)'
  },
  {
    title: "Dedicated Support",
    description: "A single point of contact for all your queries. Our team is available to assist you throughout the order lifecycle.",
    icon: Building2,
    color: 'var(--gold-accent)'
  }
];

const Services = () => {
  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 section-teal text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Our Services</h1>
          <div className="decorative-line max-w-md"></div>
          <p className="text-base sm:text-xl max-w-3xl mx-auto mt-6 text-gray-100">
            Comprehensive export solutions tailored to your business needs. From special packing to global logistics, we've got you covered.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {servicesData.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              return (
                <div 
                  key={service.id} 
                  className={`grid md:grid-cols-2 gap-8 items-center ${!isEven ? 'md:flex-row-reverse' : ''}`}
                  data-testid={`service-${service.id}`}
                >
                  <div className={`${!isEven ? 'md:order-2' : ''}`}>
                    <div className="relative h-64 sm:h-80 rounded-lg overflow-hidden shadow-xl">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary-teal)] to-transparent opacity-60"></div>
                      <div className="absolute bottom-4 left-4 flex gap-4">
                        {service.highlights.map((highlight, idx) => (
                          <div key={idx} className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg text-center">
                            <div className="text-lg font-bold" style={{ color: 'var(--primary-teal)' }}>{highlight.value}</div>
                            <div className="text-xs" style={{ color: 'var(--text-medium)' }}>{highlight.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className={`${!isEven ? 'md:order-1' : ''}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'var(--primary-teal)' }}
                      >
                        <Icon size={28} className="text-white" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--primary-teal)' }}>
                        {service.title}
                      </h2>
                    </div>
                    <p className="text-base mb-6" style={{ color: 'var(--text-medium)' }}>
                      {service.description}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <CheckCircle size={18} style={{ color: 'var(--gold-accent)' }} className="flex-shrink-0 mt-0.5" />
                          <span className="text-sm" style={{ color: 'var(--text-medium)' }}>
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 section-cream pattern-overlay">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
              How We Work
            </h2>
            <div className="decorative-line max-w-md"></div>
            <p className="text-base sm:text-lg mt-4" style={{ color: 'var(--text-medium)' }}>
              Our streamlined process ensures smooth transactions from inquiry to delivery
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 sm:gap-8">
            {processSteps.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="relative">
                  <Card className="card-hover text-center border-0 shadow-lg h-full">
                    <CardContent className="p-6">
                      <div 
                        className="text-5xl sm:text-6xl font-bold mb-4 opacity-10"
                        style={{ color: 'var(--gold-accent)' }}
                      >
                        {item.step}
                      </div>
                      <div 
                        className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full flex items-center justify-center -mt-14 sm:-mt-16"
                        style={{ backgroundColor: 'var(--primary-teal)' }}
                      >
                        <Icon size={28} className="text-white" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ color: 'var(--primary-teal)' }}>
                        {item.title}
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                  {idx < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <div 
                        className="w-8 h-0.5"
                        style={{ backgroundColor: 'var(--gold-accent)' }}
                      ></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
              Why Choose Our Services?
            </h2>
            <div className="decorative-line max-w-md"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {additionalBenefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="card-hover text-center border-0 shadow-lg">
                  <CardContent className="p-6 sm:p-8">
                    <div 
                      className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: benefit.color }}
                    >
                      <Icon size={32} className="text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ color: 'var(--primary-teal)' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--text-medium)' }}>
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-2xl p-8 sm:p-12 text-center text-white"
            style={{
              background: 'linear-gradient(135deg, var(--primary-teal) 0%, var(--primary-teal-dark) 100%)'
            }}
          >
            <h2 className="text-2xl sm:text-4xl font-bold mb-4">
              Ready to Experience Our Services?
            </h2>
            <p className="text-base sm:text-xl mb-8 text-gray-100">
              Let's discuss how we can support your business goals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-gold text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-6" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-6 bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white hover:text-[var(--primary-teal)]" data-testid="view-products-btn">
                  View Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
